import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import MIcon from '../MIcon';
import getColorFromVariant from '../../../utils/getColorFromVariant';

/**
 * Renders a desktop menu component utilizing the Material-UI framework.
 * This component allows for the display of a list of options anchored to a specified DOM element.
 * The menu supports customizable text and icon colors through theme variants and permits the execution
 * of option-specific handler functions upon selection. It also provides customizable anchor and
 * transform origins for menu positioning.
 *
 * @param {object} props - Component props.
 * @param {object} [props.anchorEl=null] - The DOM element used as the anchor for the menu's positioning.
 * @param {object} [props.anchorOrigin={vertical: 'bottom', horizontal: 'left'}] - The origin point for the anchor used by the menu.
 * @param {object} [props.transformOrigin={vertical: 'top', horizontal: 'center'}] - The origin point used for the positioned menu.
 * @param {string} [props.optionName='text'] - Specifies the object property name to use for the menu option text.
 * @param {Array} [props.options=[]] - An array of option objects for the menu. Each option can include `id`, `text`, `handler`, `icon`, `iconVariant`, `filled`, `divider`, and `textVariant`.
 * @param {boolean} [props.open=false] - Controls the open state of the menu.
 * @param {function} props.handleClose - A function to be called when the menu needs to be closed.
 * @returns {JSX.Element|null} A Material-UI Menu component populated with MenuItem components for each option, or null if `anchorEl` or `options` are not provided.
 */
const MDesktopMenu = (props) => {
  const {
    anchorEl = null,
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
    transformOrigin = { vertical: 'top', horizontal: 'center' },
    options = [],
    optionName = 'text',
    open = false,
    handleClose
  } = props;
  const theme = useTheme();

  if (!anchorEl || !options) {
    return;
  }

  return (
    <Menu
      id="m-popup-menu"
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      MenuListProps={{ 'aria-labelledby': 'menu-more-actions' }}
      open={open}
      onClose={handleClose}
    >
      {options.map((opt, idx) => {
        const handleClick = (e) => {
          handleClose(); // close desktop menu or mobile drawer
          if (opt?.handler) {
            opt.handler(e);
          } else {
            console.error('missing handler function', opt);
          }
        };

        if (opt?.divider) {
          // Divider is ignored if there is only one opt left
          if (options.length <= 2) {
            return;
          }
          return <Divider key={`${opt.id}-${idx}`} />;
        }

        return (
          <MenuItem key={`${opt.id}-${idx}`} onClick={handleClick}>
            {!!opt?.icon && (
              <ListItemIcon>
                <MIcon icon={opt.icon} variant={opt.iconVariant} filled={opt.filled} />
              </ListItemIcon>
            )}
            <ListItemText
              primary={opt[optionName]}
              secondary={opt.caption}
              sx={{ color: getColorFromVariant(theme, opt.textVariant || 'neutral'), pr: 3 }}
            />
          </MenuItem>
        );
      })}
    </Menu>
  );
};

MDesktopMenu.propTypes = {
  anchorEl: PropTypes.object,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right'])
  }),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right'])
  }),
  optionName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string,
      textVariant: PropTypes.string,
      caption: PropTypes.string,
      icon: PropTypes.string,
      iconVariant: PropTypes.string,
      filled: PropTypes.bool,
      divider: PropTypes.bool,
      handler: PropTypes.func
    })
  ).isRequired,
  open: PropTypes.bool,
  handleClose: PropTypes.func.isRequired
};

export default MDesktopMenu;
